/**
 * What each route shows until its feature is built, in the frame every
 * feature uses (day 33): the section's name over the page, the feature's name
 * in the serif, then the working surface. Until a
 * feature's day replaces its entry here, the surface says it is coming and
 * what it will hold, in a sentence or two from the PRD.
 *
 * Nothing on these pages is sample data. A placeholder that showed an invented
 * citation, report or review would be the first thing a student believed and
 * the last thing that was true.
 */

import { useEffect, useState } from "preact/hooks";
import type { PlanSummary } from "@ls/shared";
import { FEATURE_PRICES, FREE_PLAN, tierOf } from "@ls/shared/catalogue";
import type { ComponentChildren } from "preact";
import { Button, Chooser, EmptyState, FeatureGroup, FeatureTile, Icon, PageHeader, StatStrip, Wordmark } from "../ui";
import { firstName, useSession } from "./context";
import { navigate } from "./router";
import { routeById, sectionOf, type PageProps, type RouteId } from "./routes";

/**
 * The frame every feature page uses: the section's name and the feature's
 * name, from the route table, so the sidebar, the page and the tests read one
 * list. The working surface is the children: no line of description under
 * the title (Mohit, 22 October 2026).
 */
export function FeatureFrame(props: {
  readonly id: RouteId;
  /** Over the route's own: a choice inside the feature, such as CV Review. */
  readonly title?: string;
  readonly eyebrow?: string;
  readonly children: ComponentChildren;
}) {
  const route = routeById(props.id);
  const eyebrow = props.eyebrow ?? (route.section ? sectionOf(route.section).label : undefined);
  return (
    <div class="feature">
      <PageHeader {...(eyebrow ? { eyebrow } : {})} title={props.title ?? route.title} />
      {props.children}
    </div>
  );
}

/** Where a feature's working surface will be: that it is coming, and what it will do. Prices are on the Dashboard. */
export function ComingSoon(props: { readonly what: string }) {
  return (
    <section class="soon" aria-label="Coming soon">
      <EmptyState title="Coming soon" line={props.what} />
    </section>
  );
}

/**
 * A feature that is not built yet: its frame, and the coming soon state.
 *
 * Every page below is a function declaration, never a const: routes.ts and
 * this file import each other, and a declaration is ready before either
 * module's body runs, whichever of the two is loaded first.
 */
function soon(id: RouteId, what: string) {
  return (
    <FeatureFrame id={id}>
      <ComingSoon what={what} />
    </FeatureFrame>
  );
}

/* ------------------------------------------------------------------ */
/* Dashboard                                                           */
/* ------------------------------------------------------------------ */

function greeting(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * The ways into every feature, under their section's names, as the approved
 * prototype's dashboard lists them. The third level appears here, as two
 * ways into Corporate's Expert Review, and never in the sidebar.
 */
export const DASHBOARD_GROUPS: readonly { readonly label: string; readonly tiles: readonly { name: string; line: string; href: string }[] }[] = [
  {
    label: "Corporate",
    tiles: [
      { name: "CV Review", line: "By a top law firm lawyer", href: "/corporate/expert-review/cv" },
      { name: "Cover Letter Review", line: "By a top law firm lawyer", href: "/corporate/expert-review/cover-letter" },
      { name: "Expert Advice", line: "Career questions, answered by top lawyers", href: "/corporate/expert-advice" },
    ],
  },
  {
    label: "Academic",
    tiles: [
      { name: "Research Paper Review", line: "By an expert reviewer", href: "/academic/expert-review" },
      { name: "Knowledge Base", line: "Genuine research on any topic", href: "/academic/knowledge-base" },
    ],
  },
  {
    label: "Research",
    tiles: [
      { name: "Writing Assistant", line: "Corrected and improved", href: "/research/writing-assistant" },
      { name: "Plagiarism", line: "Matches against other sources", href: "/research/plagiarism" },
      { name: "AI Detection", line: "How much reads as AI written", href: "/research/ai-detection" },
      { name: "Citation", line: "Every citation, in your style", href: "/research/citation" },
    ],
  },
  {
    label: "Academy",
    tiles: [
      { name: "Courses", line: "Certification courses", href: "/academy/courses" },
      { name: "Publications", line: "HallQuill Journal of International Law", href: "/academy/publications" },
    ],
  },
];

/** Each priced feature's name on the Dashboard, in the order the sidebar has them. */
export const PRICE_NAMES: readonly { readonly id: string; readonly name: string }[] = [
  { id: "corporate-review", name: "CV Review or Cover Letter Review" },
  { id: "advice", name: "Expert Advice" },
  { id: "paper-review", name: "Research Paper Review" },
  { id: "knowledge", name: "Knowledge Base" },
  { id: "writing", name: "Writing Assistant" },
  { id: "plagiarism", name: "Plagiarism" },
  { id: "ai-detection", name: "AI Detection" },
  { id: "citation", name: "Citation" },
  { id: "courses", name: "Courses" },
  { id: "publications", name: "Publications" },
];

/**
 * The student's Credits, and what each feature takes, on the Dashboard alone
 * (Mohit, 29 October 2026: no Credit mark on the features; Credits are shown
 * here). The figures in the serif, the prices as a plain list: a name and its
 * price on a line, what is included under it, which reads at any width.
 */
export function Credits(props: { readonly plan: PlanSummary }) {
  const { plan } = props;
  const name = plan.type === "free" || !plan.tier ? FREE_PLAN.name : tierOf(plan.tier).name;
  const until = plan.expiresAt ? new Date(plan.expiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null;
  return (
    <section class="dash-credits" aria-label="Your Credits">
      <StatStrip
        label="Your Credits"
        stats={[
          { label: "Credits", value: plan.credits.toLocaleString("en-GB"), note: `${name} plan` },
          ...(until ? [{ label: "Valid until", value: until }] : []),
        ]}
      />
      <section class="card bare dash-prices" aria-labelledby="dash-prices-title">
        <h2 id="dash-prices-title">What Credits buy</h2>
        <ul>
          {PRICE_NAMES.flatMap(({ id, name: feature }) => {
            const price = FEATURE_PRICES[id];
            return price
              ? [
                  <li key={id}>
                    <p class="dash-price-line">
                      <b>{feature}</b>
                      <span>
                        {price.amount} {price.per}
                      </span>
                    </p>
                    {price.notes.length > 0 ? <p class="dash-price-notes">{price.notes.join(" ")}</p> : null}
                  </li>,
                ]
              : [];
          })}
        </ul>
      </section>
      <p class="dash-credits-foot">
        <a href="/subscriptions">Plans and Credits</a>
      </p>
    </section>
  );
}

/** Day 79 makes this the Command Center; until then, the greeting and the way into every feature. */
export function Dashboard(_: PageProps) {
  const { profile, plan } = useSession();
  const now = new Date();
  const date = now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <div class="dash">
      <PageHeader eyebrow={date} title={`${greeting(now.getHours())}, ${firstName(profile)}`} />
      <Credits plan={plan} />
      <div class="fgroups">
        {DASHBOARD_GROUPS.map((group) => (
          <FeatureGroup key={group.label} label={group.label}>
            {group.tiles.map((tile) => (
              <FeatureTile key={tile.href} {...tile} />
            ))}
          </FeatureGroup>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Corporate                                                           */
/* ------------------------------------------------------------------ */

/**
 * The one third level of PRD section 5: CV Review or Cover Letter Review,
 * chosen first, then the student works in that review alone. Each choice has
 * an address of its own under the page, /corporate/expert-review/cv, so Back
 * returns to the choice.
 */
export const REVIEW_CHOICES = [
  {
    id: "cv",
    name: "CV Review",
    line: "Read against the role you name: comments line by line, a marked up copy and an employability rating.",
    what: "Upload your CV as a PDF and name the role. It goes to the lawyer, and the review comes back here in 3 working days, laid out as a report.",
  },
  {
    id: "cover-letter",
    name: "Cover Letter Review",
    line: "Read against the role it is for: comments line by line, a marked up copy and an employability rating.",
    what: "Upload your cover letter as a PDF and name the role. It goes to the lawyer, and the review comes back here in 3 working days, laid out as a report.",
  },
] as const;

export function CorporateReview(props: PageProps) {
  const chosen = REVIEW_CHOICES.find((choice) => choice.id === props.params[0]);
  if (props.params.length > 0 && (!chosen || props.params.length > 1)) return <NotFound />;
  if (chosen) {
    return (
      <FeatureFrame id="corporate-review" eyebrow="Corporate · Expert Review" title={chosen.name}>
        <p class="choice-back">
          <a class="back" href="/corporate/expert-review">
            <Icon name="left" />
            Choose another review
          </a>
        </p>
        <ComingSoon what={chosen.what} />
      </FeatureFrame>
    );
  }
  return (
    <FeatureFrame id="corporate-review">
      <Chooser
        label="Choose a review"
        options={REVIEW_CHOICES.map((choice) => ({ id: choice.id, name: choice.name, line: choice.line, meta: "Back in 3 working days" }))}
        onPick={(id) => navigate(`/corporate/expert-review/${id}`)}
      />
    </FeatureFrame>
  );
}

export function ExpertAdvice(_: PageProps) {
  return soon(
    "advice",
    "Write your questions one at a time, each in its own box, and send them together. Each comes back answered under it, with the lawyer's role, practice area and the tier of their firm.",
  );
}

/* ------------------------------------------------------------------ */
/* Academic, Research and Academy                                      */
/* ------------------------------------------------------------------ */

export function PaperReview(_: PageProps) {
  return soon(
    "paper-review",
    "Upload your research paper, up to 5,000 words. It comes back as tracked changes, with a short letter from the reviewer.",
  );
}

/**
 * Knowledge Base (day 41), loaded with its page and never with the shell. Its
 * address is the search box, or a saved search after it: /academic/knowledge-base/<id>,
 * and one of its papers read inside Hallquill: /academic/knowledge-base/<id>/read?key=<work>
 * (29 October 2026).
 */
export function KnowledgeBase(props: PageProps) {
  const reading = props.params.length === 2 && props.params[1] === "read";
  if (props.params.length > 1 && !reading) return <NotFound />;
  const key = reading ? props.query.get("key") : null;
  if (reading && !key) return <NotFound />;
  return (
    <FeatureFrame id="knowledge">
      <KnowledgePage searchId={props.params[0] ?? null} workKey={key} />
    </FeatureFrame>
  );
}

function KnowledgePage(props: { readonly searchId: string | null; readonly workKey: string | null }) {
  const [loaded, setLoaded] = useState<typeof import("../academic/search/KnowledgeBase") | null>(null);
  const [reader, setReader] = useState<typeof import("../academic/search/Reader") | null>(null);
  useEffect(() => {
    let alive = true;
    if (props.workKey) void import("../academic/search/Reader").then((module) => alive && setReader(module));
    else void import("../academic/search/KnowledgeBase").then((module) => alive && setLoaded(module));
    return () => {
      alive = false;
    };
  }, [props.workKey !== null]);
  if (props.workKey && props.searchId) return reader ? <reader.Reader searchId={props.searchId} workKey={props.workKey} /> : null;
  return loaded ? <loaded.KnowledgeBase searchId={props.searchId} /> : null;
}

/**
 * The tool workspace (day 36), loaded with its page and never with the shell,
 * since it brings the document reader with it.
 */
function ToolWorkspace(props: { readonly tool: string; readonly note?: string }) {
  const [loaded, setLoaded] = useState<typeof import("../documents/Workspace") | null>(null);
  useEffect(() => {
    let alive = true;
    void import("../documents/Workspace").then((module) => alive && setLoaded(module));
    return () => {
      alive = false;
    };
  }, []);
  return loaded ? <loaded.Workspace {...props} /> : null;
}

/**
 * Writing Assistant's grammar marks (day 38), loaded with this page alone: the
 * page's module, its worker and the checker's engine never ride in the shell.
 */
function MarksPage() {
  const [loaded, setLoaded] = useState<typeof import("../research/writing-assistant/WritingAssistant") | null>(null);
  useEffect(() => {
    let alive = true;
    void import("../research/writing-assistant/WritingAssistant").then((module) => alive && setLoaded(module));
    return () => {
      alive = false;
    };
  }, []);
  return loaded ? <loaded.WritingAssistant /> : null;
}

/*
 * Writing Assistant is open in every build (Mohit, 30 October 2026, after day
 * 44's writing score): the grammar marks and the score, in the student's
 * browser. Until then production showed it as coming soon (days 38 and 39,
 * docs/quality/writing-assistant.md). The full refinement is day 45.
 */

export function WritingAssistant(props: PageProps) {
  // Development only: day 36's pipeline check, which proves a paper goes in
  // to the API and comes back tracked, at an address of its own.
  if (import.meta.env.DEV && props.params.length === 1 && props.params[0] === "pipeline-check") {
    return (
      <FeatureFrame id="writing" eyebrow="Research · Pipeline check">
        <ToolWorkspace
          tool="pipeline-check"
          note="Development only. The pipeline check corrects a doubled word or a double space, which proves a paper goes in and comes back with tracked changes."
        />
      </FeatureFrame>
    );
  }
  // A refined paper, opened again from History or Recents: /research/writing-assistant/<document id>.
  if (props.params.length === 1 && /^doc_[a-f0-9]{32}$/.test(props.params[0] ?? "")) {
    return (
      <FeatureFrame id="writing">
        <ReopenedPage documentId={props.params[0] as string} />
      </FeatureFrame>
    );
  }
  if (props.params.length > 0) return <NotFound />;
  return (
    <FeatureFrame id="writing">
      <MarksPage />
    </FeatureFrame>
  );
}

/** A refined paper again, loaded with this page alone, as the marks are. */
function ReopenedPage(props: { readonly documentId: string }) {
  const [loaded, setLoaded] = useState<typeof import("../research/writing-assistant/Refined") | null>(null);
  useEffect(() => {
    let alive = true;
    void import("../research/writing-assistant/Refined").then((module) => alive && setLoaded(module));
    return () => {
      alive = false;
    };
  }, []);
  return loaded ? <loaded.Reopened documentId={props.documentId} /> : null;
}

/**
 * History (Mohit, 30 October 2026): every paper and search on one page, newest
 * first, each opening its report again. Loaded with this page alone, so none of
 * it rides in the shell.
 */
export function History(props: PageProps) {
  const [loaded, setLoaded] = useState<typeof import("../history/History") | null>(null);
  useEffect(() => {
    let alive = true;
    void import("../history/History").then((module) => alive && setLoaded(module));
    return () => {
      alive = false;
    };
  }, []);
  if (props.params.length > 0) return <NotFound />;
  return <FeatureFrame id="history">{loaded ? <loaded.HistoryPage /> : null}</FeatureFrame>;
}

export function Plagiarism(_: PageProps) {
  return soon(
    "plagiarism",
    "Upload your paper. The report gives the similarity, marks every matching passage by its source, and sets each source beside your words.",
  );
}

export function AiDetection(_: PageProps) {
  return soon(
    "ai-detection",
    "Upload your paper. The report reads each paragraph, gives the overall share, and states its measured false positive rate, because no detector is proof.",
  );
}

/**
 * Citation: what the page will hold. Upload only (Mohit, 31 October 2026): a
 * list of sources on day 50, a research paper on days 51 and 52. A Knowledge
 * Base citation stays in Knowledge Base and is not listed here.
 */
export function Citation(_: PageProps) {
  return (
    <FeatureFrame id="citation">
      <ComingSoon what="Upload your research paper: every passage that needs a citation is found and given one from a real source, for you to accept. Or upload your list of sources and have it written in your style. A passage with no source found is marked, never invented." />
    </FeatureFrame>
  );
}

export function Courses(_: PageProps) {
  return soon(
    "courses",
    "Each course has lessons, assessments with worked feedback, and a certificate with a page anyone can check it on.",
  );
}

export function Publications(_: PageProps) {
  return soon(
    "publications",
    "Upload your paper as a PDF. The editors read it first, and nothing is asked of you unless they select it for review.",
  );
}

export { Subscriptions } from "../subscriptions/Subscriptions";

/* ------------------------------------------------------------------ */
/* Settings: its own module since day 13                               */
/* ------------------------------------------------------------------ */

export { Settings } from "../settings/Settings";

/* ------------------------------------------------------------------ */
/* Verify: public, outside the frame                                   */
/* ------------------------------------------------------------------ */

/**
 * Employers use this from a QR code with no account, so it renders for anyone.
 * No certificate has been issued yet, and until the register is wired to it
 * it says that rather than pretending to look one up.
 */
export function Verify(props: PageProps) {
  const id = (props.params[0] ?? "").toUpperCase();
  const [value, setValue] = useState(id);
  return (
    <div class="verify">
      <PageHeader title="Verify a certificate" />
      <form
        class="verify-form"
        onSubmit={(event) => {
          event.preventDefault();
          const next = value.trim();
          if (next) navigate(`/verify/${encodeURIComponent(next.toUpperCase())}`);
        }}
      >
        <label class="sr" for="vid">
          The certificate ID printed on the certificate
        </label>
        <input
          class="input"
          id="vid"
          value={value}
          placeholder="SL-INT-2026-XXXXXX"
          autocomplete="off"
          onInput={(event) => setValue((event.currentTarget as HTMLInputElement).value)}
        />
        <Button variant="primary" type="submit">
          Verify
        </Button>
      </form>
      {id ? (
        <p class="notice verify-result" role="status">
          Certificate verification opens when the first certificates are issued. {id} could not be checked yet.
        </p>
      ) : null}
    </div>
  );
}

/** The public pages' brand, over the page. */
export function PublicBrand() {
  return (
    <a class="public-brand" href="/" aria-label="Home">
      <Wordmark />
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* Not found                                                           */
/* ------------------------------------------------------------------ */

export function NotFound() {
  return (
    <div>
      <PageHeader title="Page not found" />
      <Button href="/">Go to the Dashboard</Button>
    </div>
  );
}
